import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api, type PackageDetail as Pkg, type WtlStatus,
  type WtlEvent, type WtlPullResult, type WtlCategoriesResp, type WtlTopicSearchResp, WTL_KIND_LABEL,
} from '../lib/api';
import { ProjectPicker, type PickedProject } from './ProjectPicker';

/**
 * "Brain · 数据管线" tab — link a package to a WTL project, pull procurement docs
 * (BOQ → work_items, drawings/scope → brief), browse the synthesized event log, and
 * publish the labor outcome back. Degrades to a disconnected banner with no creds.
 */
export function BrainPanel({ pkg }: { pkg: Pkg }) {
  const qc = useQueryClient();
  const pid = pkg.id;
  const [picking, setPicking] = useState(false);
  const [pullResult, setPullResult] = useState<WtlPullResult | null>(null);
  const [topicQ, setTopicQ] = useState('');

  const status = useQuery({ queryKey: ['wtl-status'], queryFn: () => api<WtlStatus>('/wtl/status') });
  const events = useQuery({ queryKey: ['wtl-events', pid], queryFn: () => api<WtlEvent[]>(`/packages/${pid}/wtl/events`) });

  const linkMut = useMutation({
    // G2 — send the picked project's identity so the package mirrors its name/owner/address/stage.
    mutationFn: (p: PickedProject) => api(`/packages/${pid}/wtl/link`, {
      method: 'POST',
      body: JSON.stringify({
        wtl_project_uuid: p.uuid, name: p.name,
        project_owner_name: p.project_owner_name, address: p.address, stage: p.stage, sign_date: p.sign_date,
      }),
    }),
    onSuccess: () => { setPicking(false); qc.invalidateQueries({ queryKey: ['package', pid] }); },
  });
  const unlinkMut = useMutation({
    mutationFn: () => api(`/packages/${pid}/wtl/link`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['package', pid] }),
  });
  const pullMut = useMutation({
    mutationFn: () => api<WtlPullResult>(`/packages/${pid}/wtl/pull`, { method: 'POST' }),
    onSuccess: (r) => {
      setPullResult(r);
      qc.invalidateQueries({ queryKey: ['package', pid] });
      qc.invalidateQueries({ queryKey: ['wtl-events', pid] });
    },
  });
  const publishMut = useMutation({
    mutationFn: () => api<{ ok: boolean; enabled: boolean }>(`/packages/${pid}/wtl/publish`, { method: 'POST' }),
  });
  // G4 — semantic topic search over the brain's analyzed facts (商务/合同/预算…), for answering bidder Q&A.
  const topicMut = useMutation({
    mutationFn: (query: string) => api<WtlTopicSearchResp>('/wtl/search-topic', { method: 'POST', body: JSON.stringify({ query }) }),
  });
  const clearMut = useMutation({
    mutationFn: () => api<{ events_cleared: number; files_cleared: number; brief_stripped: boolean }>(`/packages/${pid}/wtl/clear`, { method: 'POST' }),
    onSuccess: () => { setPullResult(null); qc.invalidateQueries({ queryKey: ['package', pid] }); qc.invalidateQueries({ queryKey: ['wtl-events', pid] }); },
  });

  const s = status.data;
  const configured = s?.configured;

  // v6 — top folder (+ optional 2nd-level) the published award doc is filed under (global setting).
  const categories = useQuery({ queryKey: ['wtl-categories'], queryFn: () => api<WtlCategoriesResp>('/wtl/categories'), enabled: !!configured });
  const setCatMut = useMutation({
    mutationFn: (v: { top: string | null; sub: string | null }) => api('/wtl/publish-category', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wtl-categories'] }),
  });

  return (
    <div>
      {/* connection status */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row-wrap" style={{ alignItems: 'center' }}>
          <strong>🧠 采购大脑 / WTL Brain</strong>
          {status.isLoading ? <span className="small muted">检查连接… / checking…</span>
            : configured
              ? <span className="pill pill-good">已连接 / connected · {s?.dept_code}</span>
              : <span className="pill pill-muted">未连接（缺凭证）/ disconnected (no credentials)</span>}
          <span className="spacer" />
          {configured && pkg.wtl_last_pull_at && <span className="small muted">上次拉取 / last pull: {fmt(pkg.wtl_last_pull_at)}</span>}
        </div>
        {!configured && !status.isLoading && (
          <div className="small muted" style={{ marginTop: 6 }}>
            待 WTL 管理员（邹兴兵）下发账号后，在 server/.env 配置 WTL_BASE_URL / WTL_USERNAME / WTL_PASSWORD 即自动启用。
            / Set the WTL_* env vars once credentials land — the pipeline activates automatically.
          </div>
        )}
        {configured && s?.subscriptions && s.subscriptions.length > 0 && (
          <div className="small muted" style={{ marginTop: 6 }}>
            订阅分类 / subscribed: {s.subscriptions.map((c) => c.code).join(', ')}
          </div>
        )}
      </div>

      {/* project link */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row-wrap" style={{ alignItems: 'center' }}>
          <strong className="small">关联 WTL 项目 / Linked project</strong>
          <span className="spacer" />
          {pkg.wtl_project_uuid
            ? <>
                <span className="pill pill-stage" style={{ fontFamily: 'monospace' }}>{pkg.wtl_project_uuid.slice(0, 8)}…</span>
                <button className="small" disabled={unlinkMut.isPending} onClick={() => { if (confirm('取消关联会同时清除已从大脑拉取的数据（事件+缓存+简报同步段落）。确定？\nUnlinking also clears pulled data (events + cache + brief sync sections). Continue?')) unlinkMut.mutate(); }}>取消关联 / Unlink</button>
              </>
            : <button className="small" disabled={!configured} onClick={() => setPicking((v) => !v)}>+ 关联项目 / Link</button>}
        </div>

        {picking && !pkg.wtl_project_uuid && (
          <div style={{ marginTop: 8 }}>
            <ProjectPicker
              placeholder="搜索要关联的 WTL 项目… / search a WTL project to link"
              onPick={(p) => { if (p?.uuid) linkMut.mutate(p); }}
            />
            {linkMut.isPending && <span className="small muted">关联中… / linking…</span>}
          </div>
        )}
      </div>

      {/* pull + publish actions */}
      <div className="row-wrap" style={{ gap: 8, marginBottom: 12 }}>
        <button
          className="btn-primary"
          disabled={!configured || !pkg.wtl_project_uuid || pullMut.isPending}
          onClick={() => pullMut.mutate()}
        >
          {pullMut.isPending ? '拉取中… / pulling…' : '⬇ 从大脑拉取 / Pull from Brain'}
        </button>
        <button disabled={!configured || !s?.enabled || publishMut.isPending} onClick={() => publishMut.mutate()}>
          {publishMut.isPending ? '发布中… / syncing…' : '⬆ 发布结果 / Sync to WTL'}
        </button>
        {configured && (pkg.wtl_last_pull_at || (events.data && events.data.length > 0)) && (
          <button
            disabled={clearMut.isPending}
            title="清除已拉取的数据，以便重新拉取 / clear pulled data so you can re-pull fresh"
            onClick={() => { if (confirm('清除本采购包从大脑拉取的数据（事件 + 缓存 + 简报中的同步段落），以便重新拉取？不会删除工程清单/报价。\nClear this package’s pulled data (events + cache + brief sync sections) for a fresh re-pull? Work items / bids are NOT touched.')) clearMut.mutate(); }}
          >
            {clearMut.isPending ? '清除中… / clearing…' : '🗑 清除拉取数据 / Clear pulled'}
          </button>
        )}
        {!pkg.wtl_project_uuid && <span className="small muted" style={{ alignSelf: 'center' }}>先关联项目再拉取 / link a project first</span>}
        {publishMut.isSuccess && <span className="small muted" style={{ alignSelf: 'center' }}>{publishMut.data?.enabled ? '✓ 已发布 / synced' : '同步未开启 / sync disabled'}</span>}
        {clearMut.isSuccess && <span className="small muted" style={{ alignSelf: 'center' }}>✓ 已清除 {clearMut.data?.events_cleared} 事件 / cleared</span>}
      </div>

      {/* v6 — top folder (+ optional 2nd-level) the published award doc is filed under */}
      {configured && categories.data?.tops && categories.data.tops.length > 0 && (
        <div className="row-wrap" style={{ gap: 6, marginBottom: 12, alignItems: 'center' }}>
          <span className="small muted">发布分类 / File award under (v6):</span>
          <select
            value={categories.data.selected ?? ''}
            disabled={setCatMut.isPending}
            onChange={(e) => setCatMut.mutate({ top: e.target.value || null, sub: categories.data?.selected_sub ?? null })}
            style={{ flex: '0 1 200px' }}
          >
            <option value="">未分类 / unfiled（其他部门拉不到 / not pullable）</option>
            {categories.data.tops.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input
            type="text"
            placeholder="二级子目录（可选）/ sub-folder (optional)"
            defaultValue={categories.data.selected_sub ?? ''}
            disabled={setCatMut.isPending || !categories.data.selected}
            onBlur={(e) => setCatMut.mutate({ top: categories.data?.selected ?? null, sub: e.target.value.trim() || null })}
            maxLength={200}
            style={{ flex: '0 1 200px' }}
          />
        </div>
      )}

      {pullResult && (
        <div className="card" style={{ borderColor: 'var(--good)', background: 'var(--good-soft)', marginBottom: 12 }}>
          <div className="small">
            <strong style={{ color: 'var(--good)' }}>✓ 拉取完成 / pulled</strong> · {pullResult.events_synthesized} 个事件 / events
            {pullResult.skipped_duplicates > 0 && <> · {pullResult.skipped_duplicates} 重复跳过 / dup-skipped</>}
          </div>
          {pullResult.entity_writes.length > 0 && <ul className="small" style={{ margin: '6px 0 0 16px' }}>{pullResult.entity_writes.map((w, i) => <li key={i}>{w}</li>)}</ul>}
          {pullResult.stub_todos.length > 0 && (
            <ul className="small" style={{ margin: '6px 0 0 16px', color: 'var(--muted)' }}>
              {pullResult.stub_todos.map((t, i) => <li key={i}>⚠ {t}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* G4 — topic search over analyzed facts (for answering bidder Q&A) */}
      {configured && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="row" style={{ gap: 6, alignItems: 'center' }}>
            <strong className="small">🔎 主题检索 / Topic search</strong>
            <input
              value={topicQ}
              placeholder="如：商务信息 / 预算 / 设计资料"
              onChange={(e) => setTopicQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && topicQ.trim()) topicMut.mutate(topicQ.trim()); }}
              style={{ flex: 1 }}
            />
            <button className="small" disabled={!topicQ.trim() || topicMut.isPending} onClick={() => topicMut.mutate(topicQ.trim())}>
              {topicMut.isPending ? '检索中… / searching…' : '检索 / search'}
            </button>
          </div>
          {topicMut.data && (
            <div className="small" style={{ marginTop: 8 }}>
              {topicMut.data.error && <div style={{ color: 'var(--bad)' }}>{topicMut.data.error}</div>}
              {!topicMut.data.error && topicMut.data.total === 0 && <div className="muted">未匹配到信息（文件需已上传并被大脑分析）。/ No matches — files must be uploaded and analyzed first.</div>}
              {topicMut.data.claims?.map((c, i) => (
                <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid var(--rule)' }}>
                  <span className="pill small pill-stage">{c.category_path ?? '—'}</span>{' '}
                  <strong>{c.predicate}</strong>: {c.object_text}
                  {c.chunk?.text_preview && <div className="muted" style={{ marginTop: 2 }}>“{c.chunk.text_preview.slice(0, 80)}”</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* event log */}
      <div className="small muted" style={{ marginBottom: 6 }}>数据管线事件 / Pipeline events ({events.data?.length ?? 0})</div>
      {events.data && events.data.length === 0 && <div className="empty small">暂无事件 — 关联项目并拉取。/ No events yet — link a project and pull.</div>}
      {events.data && events.data.length > 0 && (
        <div className="cmp-wrap" style={{ overflow: 'auto' }}>
          <table className="cmp" style={{ fontSize: 12 }}>
            <thead><tr><th>时间 / when</th><th>类型 / kind</th><th>文件 / file</th><th>结果 / applied</th></tr></thead>
            <tbody>
              {events.data.map((e) => (
                <tr key={e.id}>
                  <td className="muted">{fmt(e.as_of)}</td>
                  <td><span className="pill pill-stage">{WTL_KIND_LABEL[e.kind] ?? e.kind}</span></td>
                  <td>{e.source_file_name}</td>
                  <td className="small">
                    {e.entity_writes.map((w, i) => <div key={i}>✓ {w}</div>)}
                    {e.stub_todos.map((t, i) => <div key={i} className="muted">⚠ {t}</div>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}
